import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronDown, Plus, Minus, Trash, CornerDownRight } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';
import { useSelector, useDispatch } from 'react-redux';
import { updateWorkflowSessionState, fetchAdditionalContextPresets } from '../../store/reducers';
import { AdditionalContextModal } from './AdditionalContextModal';
import { isDemoMode } from '../../utils/demoUtils';
import { getAdditionalContextPresetsFromLocalStorage } from '../../utils/demoLocalStorage';

const duaSpecificationTranslations = {
  specifyDUAs: {
    eng: 'Specify DUAs and Partidas',
    esp: 'Especificar DUAs y Partidas'
  },
  optional: {
    eng: '(Optional)',
    esp: '(Opcional)'
  },
  numberOfDUAs: {
    eng: 'Number of DUAs',
    esp: 'Número de DUAs'
  },
  dua: {
    eng: 'DUA',
    esp: 'DUA'
  },
  partidas: {
    eng: 'Partidas',
    esp: 'Partidas'
  },
  numberOfPartidas: {
    eng: 'Number of Partidas',
    esp: 'Número de Partidas'
  },
  addDUA: {
    eng: 'Add DUA',
    esp: 'Agregar DUA'
  },
  removeDUA: {
    eng: 'Remove DUA',
    esp: 'Eliminar DUA'
  },
  additionalContext: {
    eng: 'Additional Context',
    esp: 'Contexto Adicional'
  }
};

function getDUASpecificationTranslation(key, isSpanish) {
  if (!duaSpecificationTranslations[key]) return key;
  return isSpanish ? duaSpecificationTranslations[key].esp : duaSpecificationTranslations[key].eng;
}

export const DUASpecification = React.memo(({ onSpecifyAmountChange, onAdditionalContextChange }) => {
  const [isSpanish] = useLanguage();
  const dispatch = useDispatch();
  
  const activeWorkflowSessionId = useSelector(state => state.workflowSessions?.activeSessionId);
  const activeWorkflowSessionState = useSelector(state => {
    const sessionId = state.workflowSessions?.activeSessionId;
    return sessionId ? state.workflowSessions?.sessions?.[sessionId]?.state : null;
  });
  
  const specifyAmount = activeWorkflowSessionState?.specifyAmount || [];
  const additionalContext = activeWorkflowSessionState?.additionalContext || '';
  const uploadComplete = activeWorkflowSessionState?.uploadComplete || false;
  const hasDocuments = activeWorkflowSessionState?.hasDocuments || false;
  
  // Initialize as collapsed (inverted logic: true = collapsed, false = show content)
  const [isExpanded, setIsExpanded] = useState(true);
  const [isAdditionalContextExpanded, setIsAdditionalContextExpanded] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openModalInCreateMode, setOpenModalInCreateMode] = useState(false);
  
  // Sync local state when workflow changes
  const [duaCount, setDuaCount] = useState(specifyAmount.length);
  const [partidasCounts, setPartidasCounts] = useState([...specifyAmount]);
  const [localAdditionalContext, setLocalAdditionalContext] = useState(additionalContext);
  
  // Get templates for quick access (max 3) from Redux
  const additionalContextPresets = useSelector((state) => {
    const presets = state.additionalContextPresets;
    return Array.isArray(presets) ? presets : [];
  });
  
  const quickAccessTemplates = useMemo(() => {
    return additionalContextPresets.slice(0, 3);
  }, [additionalContextPresets]);
  
  // Fetch presets when component mounts
  // In demo mode, load from localStorage; otherwise fetch from API
  useEffect(() => {
    const isDemo = isDemoMode();
    if (isDemo) {
      // In demo mode, load from localStorage and dispatch to Redux
      const localPresets = getAdditionalContextPresetsFromLocalStorage();
      dispatch({
        type: 'FETCH_ADDITIONAL_CONTEXT_PRESETS_SUCCESS',
        payload: localPresets
      });
    } else {
      dispatch(fetchAdditionalContextPresets());
    }
  }, [dispatch]);
  
  // Track previous values to detect changes
  const prevWorkflowSessionIdRef = useRef(activeWorkflowSessionId);
  const prevUploadCompleteRef = useRef(uploadComplete);
  const prevHasDocumentsRef = useRef(hasDocuments);
  
  // Reset to collapsed when workflow changes or files are uploaded
  useEffect(() => {
    const workflowChanged = prevWorkflowSessionIdRef.current !== activeWorkflowSessionId;
    const filesJustUploaded = (!prevUploadCompleteRef.current && uploadComplete) || (!prevHasDocumentsRef.current && hasDocuments);
    
    if (workflowChanged || filesJustUploaded) {
      setIsExpanded(true);
      setIsAdditionalContextExpanded(true);
    }
    
    // Update refs
    prevWorkflowSessionIdRef.current = activeWorkflowSessionId;
    prevUploadCompleteRef.current = uploadComplete;
    prevHasDocumentsRef.current = hasDocuments;
  }, [activeWorkflowSessionId, uploadComplete, hasDocuments]);
  
  useEffect(() => {
    setDuaCount(specifyAmount.length);
    setPartidasCounts([...specifyAmount]);
    setLocalAdditionalContext(additionalContext);
  }, [activeWorkflowSessionId, specifyAmount, additionalContext]);
  
  const handleTemplateSelect = useCallback((content) => {
    setLocalAdditionalContext(content);
    updateAdditionalContext(content);
  }, []);

  const handleDuaCountChange = (newCount) => {
    const count = Math.max(0, Math.min(100, newCount)); // Limit to 0-100
    setDuaCount(count);
    
    const newPartidasCounts = [...partidasCounts];
    if (count > newPartidasCounts.length) {
      while (newPartidasCounts.length < count) {
        newPartidasCounts.push(1);
      }
    } else if (count < newPartidasCounts.length) {
      newPartidasCounts.splice(count);
    }
    
    setPartidasCounts(newPartidasCounts);
    updateSpecifyAmount(newPartidasCounts);
  };

  const handlePartidasChange = (duaIndex, newCount) => {
    const count = Math.max(1, Math.min(100, newCount));
    const newPartidasCounts = [...partidasCounts];
    newPartidasCounts[duaIndex] = count;
    setPartidasCounts(newPartidasCounts);
    updateSpecifyAmount(newPartidasCounts);
  };

  const updateSpecifyAmount = (counts) => {
    if (activeWorkflowSessionId) {
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { specifyAmount: counts }));
    }
    onSpecifyAmountChange?.(counts);
  };
  
  const updateAdditionalContext = (value) => {
    if (activeWorkflowSessionId) {
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { additionalContext: value }));
    }
    onAdditionalContextChange?.(value);
  };

  const handleAddDUA = () => {
    handleDuaCountChange(duaCount + 1);
  };

  const handleRemoveDUA = (index) => {
    const newPartidasCounts = partidasCounts.filter((_, i) => i !== index);
    setPartidasCounts(newPartidasCounts);
    setDuaCount(newPartidasCounts.length);
    updateSpecifyAmount(newPartidasCounts);
  };

  return (
    <div className="mt-8">
        <div className="mt-4 space-y-3 transition-all duration-200">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-700 text-lg">
            {getDUASpecificationTranslation('specifyDUAs', isSpanish)}
          </span>
          {duaCount > 0 && !isExpanded && (
            <span className="text-sm text-default font-medium">
              ({duaCount} {getDUASpecificationTranslation('dua', isSpanish)}
              {!isSpanish && duaCount !== 1 ? 's' : ''})
            </span>
          )}
          <span className="text-sm text-gray-500">
            {getDUASpecificationTranslation('optional', isSpanish)}
          </span>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label={isExpanded ? 'Collapse DUAs' : 'Expand DUAs'}
          >
            <ChevronDown className={`size-4 text-gray-600 transition-transform duration-200 ${!isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {!isExpanded && (
          <div className="p-4 rounded-xl border-container">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 min-w-[140px] font-semibold">
              {getDUASpecificationTranslation('numberOfDUAs', isSpanish)}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="20"
                value={duaCount}
                onChange={(e) => handleDuaCountChange(parseInt(e.target.value) || 0)}
                className="w-20 min-w-48 max-w-48 px-3 py-1.5 text-center border-container border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-default focus:border-transparent"
              />
              <button
                onClick={() => handleDuaCountChange(duaCount - 1)}
                disabled={duaCount === 0}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={getDUASpecificationTranslation('removeDUA', isSpanish)}
              >
                <Minus className="size-4 text-gray-600" />
              </button>
              <button
                onClick={handleAddDUA}
                disabled={duaCount >= 20}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={getDUASpecificationTranslation('addDUA', isSpanish)}
              >
                <Plus className="size-4 text-gray-600" />
              </button>
            </div>
          </div>

          {duaCount > 0 && (
            <div className="mt-4 space-y-3">
              <div className="text-sm font-medium text-gray-700 mb-2 font-semibold">
                {getDUASpecificationTranslation('numberOfPartidas', isSpanish)}
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                {partidasCounts.map((partidasCount, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors justify-between"
                  >
                    <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 min-w-[128px]">
                      {getDUASpecificationTranslation('dua', isSpanish)} {index + 1}
                    </label>
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={partidasCount}
                        onChange={(e) => handlePartidasChange(index, parseInt(e.target.value) || 1)}
                        className="min-w-48 max-w-48 flex-1 px-3 py-1.5 text-center border-container border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-default focus:border-transparent"
                      />
                      <button
                        onClick={() => handlePartidasChange(index, partidasCount - 1)}
                        disabled={partidasCount <= 1}
                        className="p-2 rounded-full bg-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Minus className="size-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handlePartidasChange(index, partidasCount + 1)}
                        disabled={partidasCount >= 100}
                        className="p-2 rounded-full bg-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Plus className="size-4 text-gray-600" />
                      </button>
                    </div>
                    <span className="text-sm text-gray-500 min-w-[60px]">
                      {getDUASpecificationTranslation('partidas', isSpanish)}
                    </span>
                    </div>
                    {duaCount > 1 && (
                      <button
                        onClick={() => handleRemoveDUA(index)}
                        className="p-1.5 rounded-full hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label={`Remove ${getDUASpecificationTranslation('dua', isSpanish)} ${index + 1}`}
                      >
                        <Trash className="size-4 stroke-red-500" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {duaCount === 0 && (
            <p className="text-sm text-gray-500 italic text-left py-2">
              {isSpanish 
                ? 'No se han especificado DUAs. El sistema procesará los documentos automáticamente.'
                : 'No DUAs specified. The system will process documents automatically.'}
            </p>
          )}
          </div>
        )}

          <div className="!mt-8">
            <div className="flex items-center gap-2 mb-2">
          <span className="font-semibold text-gray-700 text-lg">
            {getDUASpecificationTranslation('additionalContext', isSpanish)}
          </span>
          <span className="text-sm text-gray-500">
            {getDUASpecificationTranslation('optional', isSpanish)}
          </span>
          <button
              onClick={() => setIsAdditionalContextExpanded(!isAdditionalContextExpanded)}
              className="p-1.5 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label={isAdditionalContextExpanded ? 'Collapse Additional Context' : 'Expand Additional Context'}
            >
              <ChevronDown className={`size-4 text-gray-600 transition-transform duration-200 ${!isAdditionalContextExpanded ? 'rotate-180' : ''}`} />
            </button>
        </div>
        {!isAdditionalContextExpanded && (
          <>
            <textarea
              value={localAdditionalContext}
              onChange={(e) => {
                const newValue = e.target.value;
                setLocalAdditionalContext(newValue);
                updateAdditionalContext(newValue);
              }}
              className="text-sm w-full px-3 py-2 border-container border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-default focus:border-transparent resize-y min-h-[100px]"
              placeholder={isSpanish ? 'Ingrese contexto adicional...' : 'Enter additional context...'}
            />
            
            {/* Quick Access Templates */}
            {quickAccessTemplates.length > 0 ? (
              <div className="mt-2 space-y-2">
                <div className="flex gap-2 flex-col w-fit">
                  {quickAccessTemplates.map((template) => {
                    const name = template.name || (isSpanish ? 'Sin nombre' : 'Untitled');
                    return (
                      <button
                        key={template.id}
                        onClick={() => handleTemplateSelect(template.content)}
                        className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition-colors flex items-center gap-1.5"
                        title={template.content}
                      >
                        <CornerDownRight className="size-3 stroke-gray-600" />
                        <span className="text-gray-600 text-xs font-medium">{name}</span>
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="mt-2a px-2 py-1.5 text-xs bg-gray-100a hover:bg-gray-100 rounded-full hover:opacity-90 transition-opacity w-fit"
                  >
                    {isSpanish ? 'Ver todas las plantillas' : 'View all templates'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                <p className="text-sm text-gray-500">
                  {isSpanish ? 'No se encontraron plantillas de contexto adicional' : 'No additional context templates found'}
                </p>
                <button
                  onClick={() => {
                    setOpenModalInCreateMode(true);
                    setIsModalOpen(true);
                  }}
                  className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition-colors flex items-center gap-1.5 w-fit"
                >
                  <Plus className="size-3 stroke-gray-600" />
                  <span className="text-gray-600 text-xs">
                    {isSpanish ? 'Agregar plantilla' : 'Add template'}
                  </span>
                </button>
              </div>
            )}
          </>
        )}
          </div>
          
          {/* Additional Context Modal */}
          <AdditionalContextModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setOpenModalInCreateMode(false);
            }}
            onSelect={handleTemplateSelect}
            isSpanish={isSpanish}
            openInCreateMode={openModalInCreateMode}
          />
        </div>
    </div>
  );
});

DUASpecification.displayName = 'DUASpecification';

export default DUASpecification;

